import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Home, Phone, Mail, ArrowLeft, Clock, DollarSign, CheckCircle2, AlertCircle, User } from 'lucide-react';
import { Logo, FooterLogo } from '../components/LogoOptions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Portal = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      const response = await axios.post(`${API}/portal/lookup`, {
        email: email.trim(),
        invoice_number: invoiceNumber.trim() || null
      });

      if (response.data.success) {
        setSubmissions(response.data.submissions);
        if (response.data.submissions.length === 0) {
          toast.info('No submissions found with this email');
        } else {
          toast.success(`Found ${response.data.submissions.length} submission(s)`);
        }
      } else {
        setSubmissions([]);
        toast.info(response.data.message || 'No submissions found');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Error searching for submissions. Please try again.');
      setSubmissions([]);
    } finally {
      setIsSearching(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300'
    };
    
    const statusLabels = {
      pending: 'Pending Review',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusStyles[status] || statusStyles.pending}`}>
        {statusLabels[status] || 'Pending Review'}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const statusStyles = {
      pending: 'bg-orange-100 text-orange-800 border-orange-300',
      paid: 'bg-green-100 text-green-800 border-green-300',
      invoice_sent: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    
    const statusLabels = {
      pending: 'Payment Pending',
      paid: 'Paid',
      invoice_sent: 'Invoice Sent'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusStyles[status] || statusStyles.pending}`}>
        {statusLabels[status] || 'Payment Pending'}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-6 text-sm">
            <a href="tel:206-627-0768" className="sm:hidden flex items-center justify-center w-10 h-10 bg-amber-500 hover:bg-amber-600 text-white rounded-full transition-colors shadow-md">
              <Phone className="w-5 h-5" />
            </a>
            <a href="tel:206-627-0768" className="hidden sm:flex items-center gap-2 text-gray-700 hover:text-amber-600 transition-colors">
              <Phone className="w-4 h-4" />
              <span className="font-medium">206-627-0768</span>
            </a>
            <a href="mailto:info@ezflowtc.com" className="hidden md:flex items-center gap-2 text-gray-700 hover:text-amber-600 transition-colors">
              <Mail className="w-4 h-4" />
              <span className="font-medium">info@ezflowtc.com</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 text-gray-600 hover:text-gray-900"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>

          {/* Portal Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
              <FileText className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Client Portal
            </h1>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              Track your transaction status and view your submission history
            </p>
          </div>

          {/* Search Form */}
          <Card className="p-8 shadow-xl mb-8" data-testid="portal-search-card">
            <form onSubmit={handleSearch} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-gray-700 font-semibold mb-2 block">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter the email you used to submit"
                  className="h-12"
                  data-testid="portal-email-input"
                  required
                />
                <p className="text-sm text-gray-500 mt-2">
                  Use the email address associated with your transaction submission
                </p>
              </div>
              
              <div>
                <Label htmlFor="invoiceNumber" className="text-gray-700 font-semibold mb-2 block">
                  Invoice Number <span className="text-gray-400">(Optional)</span>
                </Label>
                <Input
                  id="invoiceNumber"
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="INV-XXXXXXXX-XXXX"
                  className="h-12"
                  data-testid="portal-invoice-input"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Optional: Enter your invoice number to find a specific submission
                </p>
              </div>

              <Button
                type="submit"
                disabled={isSearching}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-semibold"
                data-testid="portal-search-btn"
              >
                {isSearching ? (
                  <>
                    <Search className="w-5 h-5 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-5 h-5 mr-2" />
                    Find My Submissions
                  </>
                )}
              </Button>
            </form>
          </Card>

          {/* Results */}
          {hasSearched && (
            <div data-testid="portal-results">
              {submissions.length === 0 ? (
                <Card className="p-8 text-center">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Submissions Found</h3>
                  <p className="text-gray-500 mb-6">
                    We couldn't find any submissions with this email address. Please check the email and try again.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/')}
                    className="mx-auto"
                  >
                    Submit a New Transaction
                  </Button>
                </Card>
              ) : (
                <div className="space-y-4">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Your Submissions ({submissions.length})
                  </h2>
                  
                  {submissions.map((submission) => (
                    <Card 
                      key={submission.submission_id} 
                      className="p-6 hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-amber-500"
                      data-testid={`submission-card-${submission.submission_id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Home className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900 text-lg">
                                {submission.property_address}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {submission.service_type}
                              </p>
                            </div>
                          </div>
                          
                          <div className="grid sm:grid-cols-2 gap-4 mt-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <FileText className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">{submission.invoice_number}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span>{submission.submission_date}</span>
                            </div>
                            {submission.listing_agent && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <User className="w-4 h-4 text-gray-400" />
                                <span>Listing: {submission.listing_agent}</span>
                              </div>
                            )}
                            {submission.buyer_agent && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <User className="w-4 h-4 text-gray-400" />
                                <span>Buyer: {submission.buyer_agent}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row lg:flex-col gap-2 lg:items-end">
                          {getStatusBadge(submission.status)}
                          {getPaymentBadge(submission.payment_status)}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Help Section */}
          <Card className="p-6 mt-10 bg-gradient-to-r from-gray-50 to-white border-gray-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Need Help?</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Have questions about your transaction or can't find your submission?
                </p>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="tel:206-627-0768" 
                    className="text-amber-600 hover:text-amber-700 font-semibold text-sm flex items-center gap-1"
                  >
                    <Phone className="w-4 h-4" />
                    206-627-0768
                  </a>
                  <a 
                    href="mailto:info@ezflowtc.com" 
                    className="text-amber-600 hover:text-amber-700 font-semibold text-sm flex items-center gap-1"
                  >
                    <Mail className="w-4 h-4" />
                    info@ezflowtc.com
                  </a>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6">
              <div className="flex justify-center mb-4">
                <FooterLogo />
              </div>
              <p className="text-gray-400 max-w-md mx-auto">
                Professional transaction coordination services for real estate agents and brokers.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8 pb-8 border-b border-gray-700">
              <a href="tel:206-627-0768" className="flex items-center gap-2 text-gray-300 hover:text-amber-500 transition-colors">
                <Phone className="w-5 h-5" />
                <span className="font-medium">206-627-0768</span>
              </a>
              <a href="mailto:info@ezflowtc.com" className="flex items-center gap-2 text-gray-300 hover:text-amber-500 transition-colors">
                <Mail className="w-5 h-5" />
                <span className="font-medium">info@ezflowtc.com</span>
              </a>
            </div>
            
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} EZ Flow TC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Portal;
